import React from 'react';
const { Icon, Button, Card } = window;
/* ============================================================
   BOOKER — Tela: Monitoramento da pasta local de contratos
   Lê os arquivos reais servidos pelo backend (pasta CONTRACTS_DIR).
   ============================================================ */
var { useState, useEffect, useMemo } = React;

const fmtTamanho = (b) => {
  if (b == null) return '—';
  if (b < 1024) return b + ' B';
  if (b < 1024 * 1024) return (b / 1024).toFixed(0) + ' KB';
  return (b / 1024 / 1024).toFixed(1) + ' MB';
};
function fmtRel(iso) {
  try {
    const d = new Date(iso), agora = new Date();
    const seg = Math.max(0, (agora - d) / 1000);
    if (seg < 3600) return `há ${Math.floor(seg / 60)} min`;
    if (seg < 86400) return `há ${Math.floor(seg / 3600)} h`;
    const dias = Math.floor(seg / 86400);
    if (dias === 1) return 'ontem';
    if (dias < 30) return `há ${dias} dias`;
    return d.toLocaleDateString('pt-BR');
  } catch { return '—'; }
}

function Monitor({ onNavigate, account }) {
  const DB = window.DB;
  const email = account ? account.email : '—';
  const [estado, setEstado] = useState('carregando'); // carregando | ok | erro
  const [pasta, setPasta] = useState('');
  const [fonte, setFonte] = useState('');
  const [arquivos, setArquivos] = useState([]);
  const [erro, setErro] = useState('');
  const [escaneando, setEscaneando] = useState(false);
  const [ultima, setUltima] = useState('—');
  const [sp, setSp] = useState(null); // status da integração SharePoint
  const [pend, setPend] = useState([]); // arquivos no SharePoint ainda não importados
  const [sincronizando, setSincronizando] = useState(false);

  async function carregar(scan = false) {
    if (scan) setEscaneando(true); else setEstado('carregando');
    setErro('');
    try {
      const d = await window.Api.apiContracts();
      setPasta(d.pasta || '');
      setFonte(d.fonte || '');
      setArquivos(d.arquivos || []);
      setEstado('ok');
      window.Api.apiSharepointStatus().then(setSp).catch(() => setSp(null));
      window.Api.apiSharepointPendentes().then(d => setPend((d && d.pendentes) || [])).catch(() => setPend([]));
      try { setUltima(new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })); } catch { setUltima('agora'); }
    } catch (e) {
      setErro(e.message || 'Falha ao carregar os contratos'); setEstado('erro');
    } finally {
      if (scan) setEscaneando(false);
    }
  }
  useEffect(() => { carregar(); }, []);

  const podeEditar = window.podeEditar ? window.podeEditar() : true;
  async function importarPend(p) {
    try {
      const r = await window.Api.apiSharepointImportar(p.id);
      window.avisar(r.tipoDoc === 'aditivo' ? 'Importado como aditivo (foi pra aba Aditivos).'
        : r.revisar ? 'Importado como contrato — marcado como "a revisar" (faltou o valor).'
        : `Contrato importado${r.razao ? ': ' + r.razao : ''}. ✅`);
      carregar(true);
    } catch (e) {
      if (e.duplicado) {
        // já existe contrato p/ esse CNPJ. Dois casos possíveis:
        // (a) é um CONTRATO DIFERENTE do mesmo cliente -> importar como novo (force)
        // (b) é o MESMO contrato (outra versão do arquivo) -> ligar ao existente
        const outro = await window.confirmar({
          titulo: 'Já existe contrato para este CNPJ',
          mensagem: `${e.message}\n\nEste arquivo é um contrato DIFERENTE do mesmo cliente (ex.: um segundo posto/serviço)?\n\n• Sim → cria um segundo contrato, separado, para o mesmo CNPJ.\n• Não → é a mesma coisa (outra via do arquivo já cadastrado).`,
          confirmar: 'Sim, é outro contrato',
          cancelar: 'Não',
        });
        if (outro) {
          try {
            const r = await window.Api.apiSharepointImportar(p.id, true);
            window.avisar(`Segundo contrato importado${r.razao ? ': ' + r.razao : ''} — mesmo CNPJ, contrato separado. ✅`);
            carregar(true);
          } catch (err) { window.avisar(err.message); }
        } else {
          const ligar = await window.confirmar({
            titulo: 'Ligar ao contrato existente?',
            mensagem: 'Então é o mesmo contrato — vou apenas ligar este arquivo ao que já existe, sem criar duplicata.',
            confirmar: 'Ligar ao existente', cancelar: 'Cancelar',
          });
          if (ligar) {
            try {
              const r = await window.Api.apiSharepointVincularExistente(p.id);
              window.avisar(`Arquivo ligado ao contrato existente${r.razao ? ': ' + r.razao : ''}. ✅`);
              carregar(true);
            } catch (err) { window.avisar(err.message); }
          }
        }
      } else window.avisar(e.message);
    }
  }
  async function sincronizar() {
    setSincronizando(true);
    try {
      const r = await window.Api.apiSharepointSincronizar();
      if (r && r.ok) window.avisar(`Sincronizado! ${r.ligados} ligado(s), ${r.religados} religado(s)` + (r.novos ? `, ${r.novos} novo(s) a revisar` : '') + (r.quebrados ? `, ${r.quebrados} sem arquivo (usando o banco)` : '') + '.');
      else window.avisar((r && r.erro) || 'Falha ao sincronizar.');
      carregar(true);
    } catch (e) { window.avisar(e.message); }
    finally { setSincronizando(false); }
  }
  async function dispensarPend(p) {
    if (!await window.confirmar(`Dispensar "${p.nome}" da lista de revisão? (não apaga nada do SharePoint — só some daqui, ex.: arquivo duplicado)`)) return;
    try { await window.Api.apiSharepointIgnorar(p.id); carregar(true); }
    catch (e) { window.avisar(e.message); }
  }

  // contratos reais marcados "a revisar" (da extração)
  const aRevisar = useMemo(() => {
    try { return DB.contratosComCliente().filter(k => k.revisar); } catch { return []; }
  }, [estado]);

  // feed de atividade: arquivos por data de modificação (mais recentes primeiro)
  const feed = useMemo(() => {
    return [...arquivos]
      .sort((a, b) => new Date(b.modificadoEm) - new Date(a.modificadoEm))
      .slice(0, 10);
  }, [arquivos]);

  const fonteBanco = fonte !== 'pasta';
  const nomePasta = fonteBanco ? 'Banco de dados' : (pasta ? pasta.split('/').filter(Boolean).pop() : 'Pasta local');
  const spOk = sp && sp.conectado;
  const origemLabel = spOk ? 'SharePoint' : (fonteBanco ? 'Banco de dados' : 'Pasta local');

  return (
    <div style={{ animation: 'fadeUp .4s ease both' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 22 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-.02em' }}>Monitoramento dos contratos</h1>
          <p style={{ margin: '5px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Contratos armazenados e monitorados · origem: <b style={{ color: 'var(--text)' }}>{origemLabel}</b></p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {podeEditar
            ? <Button variant="accent" icon="refresh" onClick={sincronizar}>{sincronizando ? 'Sincronizando…' : 'Sincronizar SharePoint'}</Button>
            : <Button variant="primary" icon="refresh" onClick={() => carregar(true)}>{escaneando ? 'Atualizando…' : 'Atualizar'}</Button>}
        </div>
      </div>

      {/* Barra de status da conexão */}
      <Card pad={0} style={{ marginBottom: 18, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 22px', flexWrap: 'wrap' }}>
          <span style={{ position: 'relative', width: 44, height: 44, borderRadius: 12, background: 'var(--ok-bg)', color: 'var(--ok)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name="folder" size={22} />
            <span style={{ position: 'absolute', bottom: -2, right: -2, width: 14, height: 14, borderRadius: 99, background: estado === 'erro' ? 'var(--danger)' : 'var(--ok)', border: '2px solid var(--surface)' }} />
          </span>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>
              {estado === 'erro' ? 'Origem indisponível' : (spOk ? 'SharePoint conectado' : (fonteBanco ? 'Banco de dados conectado' : 'Pasta local conectada'))}
              {estado !== 'erro' && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 700, color: 'var(--ok)', background: 'var(--ok-bg)', padding: '2px 8px', borderRadius: 99 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 99, background: 'var(--ok)', animation: 'pulse 2s infinite' }} /> ativa
                </span>
              )}
            </div>
            <div className="mono" style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, wordBreak: 'break-all' }}>{spOk && sp.site ? sp.site.nome : (pasta || '—')}</div>
          </div>
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
            <StatBit label="Backend" value={email} />
            <StatBit label="Arquivos" value={arquivos.length + ' contratos'} />
            <StatBit label="Última verificação" value={escaneando ? 'verificando…' : ultima} tone={escaneando ? 'var(--booker-orange)' : 'var(--text-strong)'} />
          </div>
        </div>
        {escaneando && <div style={{ height: 3, background: 'var(--surface-inset)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: '40%', background: 'linear-gradient(90deg, transparent, var(--booker-orange), transparent)', animation: 'slide 1.2s linear infinite' }} />
        </div>}
      </Card>

      {estado === 'erro' && (
        <Card pad={24} style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
          <span style={{ width: 42, height: 42, borderRadius: 11, background: 'var(--danger-bg)', color: 'var(--danger)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="alert" size={20} /></span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)' }}>Não foi possível carregar os contratos</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{erro} — verifique se o backend está rodando em <span className="mono">{window.Api?.API_BASE}</span>.</div>
          </div>
          <Button variant="default" onClick={() => carregar(true)}>Tentar de novo</Button>
        </Card>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 18, alignItems: 'start' }}>
        {/* Coluna esquerda: pasta + a revisar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 14 }}>
              <Icon name="folder" size={18} style={{ color: 'var(--booker-orange)' }} />
              <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>Fonte dos contratos</h3>
            </div>

            {/* SharePoint — fonte principal */}
            <Fonte
              principal
              tom={sp == null ? 'aguardando' : sp.conectado ? 'ok' : sp.configurado ? 'erro' : 'off'}
              titulo="SharePoint — fonte principal"
              nome={sp && sp.site ? sp.site.nome : (sp && !sp.configurado ? 'Não configurado' : (sp && sp.erro ? 'Indisponível agora' : '—'))}
              url={sp && sp.site ? sp.site.url : ''}
              detalhe={sp == null ? 'verificando…'
                : sp.conectado
                  ? `${sp.contratosLigados} de ${sp.contratosTotal} contratos ligados` + (sp.site && sp.site.pasta_contratos != null ? ` · ${sp.site.pasta_contratos} PDFs na pasta` : '')
                  : (sp.erro || 'sem conexão')}
            />

            {/* link direto pra pasta de contratos no SharePoint */}
            {sp && sp.site && (sp.site.pasta_contratos_url || sp.site.url) && (
              <a href={sp.site.pasta_contratos_url || sp.site.url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 9, fontSize: 12.5, fontWeight: 700, color: 'var(--booker-orange)', textDecoration: 'none' }}>
                <Icon name="external" size={14} /> Abrir a pasta de contratos no SharePoint
              </a>
            )}

            {/* Banco — reserva */}
            <div style={{ marginTop: 10 }}>
              <Fonte
                tom="reserva"
                titulo="Banco de dados — reserva"
                nome="Usado só se o SharePoint falhar"
                detalhe={`${arquivos.length} PDFs guardados${sp ? ` · ${sp.contratosLigados} já vêm do SharePoint` : ''}`}
              />
            </div>

            <div style={{ display: 'flex', gap: 9, marginTop: 12, padding: '10px 12px', background: 'var(--surface-inset)', borderRadius: 'var(--r-sm)', fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
              <Icon name="settings" size={15} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>Os contratos são lidos direto do <b style={{ color: 'var(--text)' }}>SharePoint</b> (basta lançar o PDF na pasta). O <b style={{ color: 'var(--text)' }}>banco</b> fica como reserva e guarda os dados extraídos (valores, rateio).</span>
            </div>
          </Card>

          {/* Contratos a revisar (do sistema) + pendentes no SharePoint (ainda não importados) */}
          <Card pad={0}>
            <div style={{ padding: '18px 20px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>Contratos a revisar</h3>
              {(aRevisar.length + pend.length) > 0 && <span style={{ background: 'var(--warn-bg)', color: 'var(--booker-orange)', fontWeight: 800, fontSize: 12, padding: '2px 8px', borderRadius: 99 }}>{aRevisar.length + pend.length}</span>}
            </div>

            {/* 1) contratos JÁ no sistema, marcados a revisar */}
            {aRevisar.slice(0, 8).map((k) => (
              <div key={k.id} onClick={() => onNavigate('detalhe', k.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderTop: '1px solid var(--border)', cursor: 'pointer', transition: 'background .14s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--danger-bg)', color: 'var(--danger)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="alert" size={16} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.cliente.fantasia}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{!k.valorAtual ? 'Valor não identificado' : 'Conferir dados extraídos'}</div>
                </div>
                <Icon name="chevronR" size={16} style={{ color: 'var(--text-faint)' }} />
              </div>
            ))}

            {/* 2) arquivos NO SHAREPOINT ainda não importados (pendentes) */}
            {pend.slice(0, 12).map((p) => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
                <span style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--warn-bg)', color: 'var(--booker-orange)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="link" size={15} /></span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.nome}</div>
                  <div style={{ fontSize: 11, color: 'var(--booker-orange)', fontWeight: 700 }}>No SharePoint · ainda não importado</div>
                </div>
                <Button size="sm" variant="ghost" icon="external" onClick={() => window.Api.abrirSharepoint(p.id).catch(e => window.avisar(e.message))}>Abrir</Button>
                {podeEditar && <Button size="sm" variant="accent" icon="download" onClick={() => importarPend(p)}>Importar</Button>}
                {podeEditar && <Button size="sm" variant="ghost" icon="x" onClick={() => dispensarPend(p)}>Dispensar</Button>}
              </div>
            ))}

            {(aRevisar.length + pend.length) === 0 && (
              <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13, borderTop: '1px solid var(--border)' }}>Nenhum contrato pendente de revisão. 🎉</div>
            )}
          </Card>
        </div>

        {/* Coluna direita: feed de atividade (arquivos reais) */}
        <Card pad={0}>
          <div style={{ padding: '18px 22px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-strong)' }}>Arquivos (PDFs)</h3>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 7, height: 7, borderRadius: 99, background: 'var(--ok)', animation: 'pulse 2s infinite' }} /> {arquivos.length} itens</span>
          </div>
          <div style={{ padding: '6px 0', maxHeight: 560, overflowY: 'auto' }}>
            {estado === 'carregando' && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Carregando…</div>}
            {feed.map((ev, i) => (
              <div key={ev.nome} style={{ display: 'flex', gap: 14, padding: '13px 22px', alignItems: 'center' }}>
                <span style={{ width: 34, height: 34, borderRadius: 9, background: ev.ext === 'pdf' ? 'var(--danger-bg)' : 'var(--info-bg)', color: ev.ext === 'pdf' ? 'var(--danger)' : 'var(--info)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 800, fontSize: 9.5 }}>{ev.ext.toUpperCase()}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.nome}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{fmtTamanho(ev.tamanho)} · modificado {fmtRel(ev.modificadoEm)}</div>
                </div>
                <Button size="sm" variant="ghost" icon="external" onClick={() => window.Api.abrirArquivo(ev.nome)}>Abrir</Button>
              </div>
            ))}
            {estado === 'ok' && !feed.length && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Nenhum arquivo encontrado.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Fonte({ principal, tom, titulo, nome, url, detalhe }) {
  const cor = tom === 'ok' ? 'var(--ok)' : tom === 'erro' ? 'var(--danger)' : tom === 'reserva' ? 'var(--info)' : 'var(--text-faint)';
  const bg = tom === 'ok' ? 'var(--ok-bg)' : tom === 'erro' ? 'var(--danger-bg)' : tom === 'reserva' ? 'var(--info-bg)' : 'var(--surface-inset)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '12px 13px', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)', border: `1px solid ${principal ? cor + '55' : 'var(--border)'}` }}>
      <span style={{ width: 34, height: 34, borderRadius: 9, background: bg, color: cor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={principal ? 'link' : 'folder'} size={17} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--text-strong)' }}>{titulo}</span>
          <span style={{ width: 7, height: 7, borderRadius: 99, background: cor, flexShrink: 0 }} />
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nome}</div>
        <div style={{ fontSize: 11, fontWeight: 600, color: cor, marginTop: 1 }}>{detalhe}</div>
      </div>
    </div>
  );
}

function StatBit({ label, value, tone, mono }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap' }}>{label}</div>
      <div className={mono ? 'mono' : ''} style={{ fontSize: 14, fontWeight: 700, color: tone || 'var(--text-strong)', marginTop: 3, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}

Object.assign(window, { Monitor });
